import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { kickoffProductPdfSourceUploads } from '@/features/product-catalog/lib/kickoffProductPdfSourceUploads'
import { useTranslation } from 'react-i18next'

export function useProductFileSourceUpload(productId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('productCatalog')

  const [uploadingSourceIds, setUploadingSourceIds] = useState<Set<string>>(
    () => new Set()
  )

  const runKickoffForFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return
      }
      await kickoffProductPdfSourceUploads({
        productId,
        files,
        queryClient,
        translate: t,
        tracking: {
          onSourceAdded: (sourceId) => {
            setUploadingSourceIds((previous) =>
              new Set(previous).add(sourceId)
            )
          },
          onSourceFinished: (sourceId) => {
            setUploadingSourceIds((previous) => {
              const next = new Set(previous)
              next.delete(sourceId)
              return next
            })
          },
        },
      })
    },
    [productId, queryClient, t]
  )

  const handleFileSelect = useCallback(
    async (file: File) => {
      await runKickoffForFiles([file])
    },
    [runKickoffForFiles]
  )

  const handleMultiFileSelect = useCallback(
    async (files: File[]) => {
      await runKickoffForFiles(files)
    },
    [runKickoffForFiles]
  )

  return { handleFileSelect, handleMultiFileSelect, uploadingSourceIds }
}
